import { useGetRequestsQuery } from '../services/requests.service';
import { useGetUsersQuery } from '../../Users/services';
import { useMemo } from 'react';

interface UseRequestsProps {
  paginated: {
    page: number;
    pageSize: number;
  };
  userId?: string;
  statuses?: string[];
  assetTypeId?: string[];
}

const useRequests = ({ paginated, userId, statuses, assetTypeId }: UseRequestsProps) => {
  const queryParams = useMemo(
    () => ({
      paginated,
      ...(userId && userId.trim() !== '' ? { userId } : {}),
      ...(statuses && statuses.length > 0 ? { statuses } : {}),
      ...(assetTypeId && assetTypeId.length > 0 ? { assetTypeId } : {}),
    }),
    [paginated.page, paginated.pageSize, userId, statuses, assetTypeId]
  );

  const {
    data: requests,
    isLoading: isLoadingRequests,
    isError: isErrorRequests,
  } = useGetRequestsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
  });

  const usersQueryParams = useMemo(
    () => ({
      paginated: { page: 1, pageSize: 1000 },
    }),
    []
  );

  const { data: users, isLoading: isLoadingUsers } =
    useGetUsersQuery(usersQueryParams);

  const usersMap = useMemo(() => {
    const map = new Map();
    users?.items?.forEach((user) => {
      map.set(user.id, user);
    });

    return map;
  }, [users?.items]);

  const requestsWithUsers = useMemo(() => {
    const mapped =
      requests?.items?.map((request) => {
        const admin = usersMap.get(request.adminId);
        const user = usersMap.get(request.userId);

        let handledBy = null;
        if (request.handledBy && request.handledBy.trim() !== '') {
          handledBy = { id: request.handledBy, fullName: request.handledBy };
        }

        return {
          ...request,
          admin,
          user,
          handledBy,
        };
      }) || [];

    return mapped;
  }, [requests?.items, usersMap]);

  return {
    requests: requestsWithUsers,
    totalItems: requests?.count || 0,
    totalPages: requests?.count
      ? Math.ceil(requests.count / paginated.pageSize)
      : 0,
    isLoading: isLoadingRequests || isLoadingUsers,
    isError: isErrorRequests,
  };
};

export default useRequests;
